
import { Component, Input, Output, EventEmitter, ViewChild, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from '@angular/material/icon';

export interface DateRangeResult {
  startDate: string | null;
  endDate: string | null;
  startTime?: string;
  endTime?: string;
  startDateObj?: Date | null;
  endDateObj?: Date | null;
  leftCalendarMonth?: Date | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-date-range-picker',
  standalone: true,
  host: {
    '[class.single-date]': 'isSingleDate',
    '[class.align-right]': 'alignRight'
  },
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatIconModule,
    MatNativeDateModule,
    MatButtonModule,
    MatInputModule
  ],
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss']
})
export class DateRangePickerComponent implements OnInit, OnChanges {
  @Input() startDate!: string | null;
  @Input() endDate!: string | null;
  @Input() dataQaPrefix: string | null = null;
  @Input() dataPwPrefix: string | null = null;
  @Input() disabled = false;
  @Input() isSingleDate = false;
  @Input() alignRight = false;
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() timeIntervalMinutes = 30;
  @Output() rangeChange = new EventEmitter<DateRangeResult>();
  @Output() startDateChange = new EventEmitter<string | null>();
  @Output() endDateChange = new EventEmitter<string | null>();
  @ViewChild('startCalendar') startCalendar!: MatCalendar<Date>;
  @ViewChild('endCalendar') endCalendar!: MatCalendar<Date>;
  @Output() cancelled = new EventEmitter<void>();

  showPicker: boolean = false;
  displayDateValue: string = '';
  displayTimeValue: string = '';
  startDateObj: Date | null = null;
  endDateObj: Date | null = null;
  startTime: string | null = null;
  endTime: string | null = null;
  leftCalendarMonth: Date = new Date();
  snapshot: DateRangeResult | null = null;

  timeSlots: string[] = [];

  // Right calendar always shows next month
  get rightCalendarStart(): Date {
    const baseDate = this.endDateObj ?? this.startDateObj ?? new Date();
    const nextMonth = new Date(baseDate); // clone

    if (!this.endDateObj || (this.startDateObj && this.endDateObj.getMonth() === this.startDateObj.getMonth())) {
      nextMonth.setMonth(nextMonth.getMonth() + 1);
    }

    return nextMonth;
  }

  constructor() {}

  // Optional: If you want the left calendar to always start at current month initially
  ngOnInit() {
    this.generateTimeSlots();
    this.initDateTime();
    this.setDisplayDateTime();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['startDate'] || changes['endDate']) {
      this.initDateTime();
      this.setDisplayDateTime();
    }
  }

  generateTimeSlots() {
    const slots: string[] = [];

    for (let totalMinutes = 0; totalMinutes < 24 * 60; totalMinutes += this.timeIntervalMinutes) {
      const hour = Math.floor(totalMinutes / 60);
      const min = totalMinutes % 60;

      const date = new Date();
      date.setHours(hour, min, 0, 0);

      const formatted = date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      slots.push(formatted);
    }

    this.timeSlots = slots;
  }

  initDateTime() {
    this.startDateObj = this.startDate ? this.parseFromIsoLocalClock(this.startDate) : null;
    this.endDateObj = this.endDate ? this.parseFromIsoLocalClock(this.endDate) : null;
    this.startTime = this.startDateObj
      ? this.startDateObj.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
      : null;
    this.endTime = this.endDateObj
      ? this.endDateObj.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
      : null;
    this.captureSnapshot();
  }

  captureSnapshot() {
    if (this.startDateObj) {
      this.leftCalendarMonth = new Date(this.startDateObj.getFullYear(), this.startDateObj.getMonth(), 1);
    } else {
      this.leftCalendarMonth = new Date(); // current month
    }
    this.snapshot = {
      startDate: this.startDate,
      endDate: this.endDate,
      startTime: this.startTime ?? undefined,
      endTime: this.endTime ?? undefined,
      startDateObj: this.startDateObj,
      endDateObj: this.endDateObj,
      leftCalendarMonth: this.leftCalendarMonth
    };
  }

  onDateSelected(date: Date | null) {
    if (!date) return;

    const selected = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (this.isSingleDate) {
      this.startDate = this.parseToIsoLocalCLock(selected);
      this.startDateObj = selected;
      this.endDate = null;
      this.endDateObj = null;
      this.refreshCalendars();
      return;
    }

    if (!this.startDate || (this.startDate && this.endDate)) {
      this.startDate = this.parseToIsoLocalCLock(selected);
      this.startDateObj = selected;
      this.endDate = null;
      this.endDateObj = null;
    } else {
      const start = this.parseFromIsoLocalClock(this.startDate);

      if (selected < start) {
        this.endDate = this.startDate;
        this.endDateObj = this.parseFromIsoLocalClock(this.startDate);

        this.startDate = this.parseToIsoLocalCLock(selected);
        this.startDateObj = selected;
      } else {
        this.endDate = this.parseToIsoLocalCLock(selected);
        this.endDateObj = selected;
      }
    }

    this.refreshCalendars();
  }

  applyDateAndTime() {
    const combinedStart = this.combineDateAndTime(this.startDate, this.startTime);
    const combinedEnd = this.isSingleDate ? null : this.combineDateAndTime(this.endDate, this.endTime);

    this.startDate = combinedStart ? combinedStart : null;
    this.endDate = this.isSingleDate ? null : combinedEnd ? combinedEnd : null;

    this.startDateObj = this.startDate ? this.parseFromIsoLocalClock(this.startDate) : null;
    this.endDateObj = this.endDate ? this.parseFromIsoLocalClock(this.endDate) : null;

    if (!this.validateRange()) {
      return;
    }

    this.setDisplayDateTime();
    this.showPicker = false;
    this.captureSnapshot();

    this.rangeChange.emit({
      startDate: this.startDate,
      endDate: this.endDate,
      startTime: this.startTime ?? undefined,
      endTime: this.endTime ?? undefined,
      startDateObj: this.startDateObj,
      endDateObj: this.endDateObj,
      leftCalendarMonth: this.leftCalendarMonth
    });

    this.startDateChange.emit(this.startDate);
    this.endDateChange.emit(this.endDate);
  }

  cancel() {
    this.showPicker = false;
    if (this.snapshot) {
      this.startDate = this.snapshot.startDate;
      this.endDate = this.snapshot.endDate;
      this.startTime = this.snapshot.startTime ?? null;
      this.endTime = this.snapshot.endTime ?? null;
      this.startDateObj = this.snapshot.startDateObj!;
      this.endDateObj = this.snapshot.endDateObj!;
      this.leftCalendarMonth = this.snapshot.leftCalendarMonth!;
    }
    // implement if needed
    this.cancelled.emit();
  }

  // Force Angular Material calendar to re-render
  refreshCalendars() {
    if (this.startCalendar) this.startCalendar.updateTodaysDate();
    if (this.endCalendar) this.endCalendar.updateTodaysDate();
  }

  dateClass = (d: Date) => {
    if (!this.startDate) return '';

    // Only compare the date part
    const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const start = this.startDate ? this.parseFromIsoLocalClock(this.startDate) : null;
    const end = this.endDate ? this.parseFromIsoLocalClock(this.endDate) : start;

    if (!start) return '';

    // normalize start and end to ignore time
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = end ? new Date(end.getFullYear(), end.getMonth(), end.getDate()) : startDay;

    if (date.getTime() === startDay.getTime()) return 'range-start';
    if (date.getTime() === endDay.getTime()) return 'range-end';
    if (date > startDay && date < endDay) return 'range-middle';

    return '';
  };

  private setDisplayDateTime() {
    if (this.isSingleDate) {
      this.displayDateValue = this.startDateObj ? this.formatDisplayDate(this.startDateObj) : '';
      this.displayTimeValue = this.startTime ?? '';
      return;
    }
    this.displayDateValue =
      this.startDateObj && this.endDateObj
        ? `${this.formatDisplayDate(this.startDateObj)} - ${this.formatDisplayDate(this.endDateObj)}`
        : '';
    this.displayTimeValue = this.startTime && this.endTime ? `${this.startTime} - ${this.endTime}` : '';
  }

  validationMessage: string | null = null;

  private isSameDay(start: Date | null, end: Date | null): boolean {
    if (!start || !end) return false;

    return (
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate()
    );
  }

  private parseTimeToMinutes(time: string | null): number | null {
    if (!time) return null;

    const [timePart, modifier] = time.trim().split(/\s+/);
    const [rawHour, minutes] = timePart.split(':').map(Number);

    let hours = rawHour;

    if (modifier?.toUpperCase() === 'PM' && hours !== 12) {
      hours += 12;
    }

    if (modifier?.toUpperCase() === 'AM' && hours === 12) {
      hours = 0;
    }

    return hours * 60 + minutes;
  }

  private validateRange(): boolean {
    this.validationMessage = null;

    if (!this.startDateObj) {
      this.validationMessage = 'Start date is required.';
      return false;
    }

    if (!this.isSingleDate && !this.endDateObj) {
      this.validationMessage = 'End date is required.';
      return false;
    }

    if (!this.isSingleDate && this.startDateObj && this.endDateObj && this.endDateObj < this.startDateObj) {
      this.validationMessage = 'End date cannot be before start date.';
      return false;
    }

    if (!this.isSingleDate && this.isSameDay(this.startDateObj, this.endDateObj)) {
      const startMinutes = this.parseTimeToMinutes(this.startTime);
      const endMinutes = this.parseTimeToMinutes(this.endTime);

      if (startMinutes !== null && endMinutes !== null && endMinutes <= startMinutes) {
        this.validationMessage = 'End time must be later than start time when start and end dates are the same.';
        return false;
      }
    }

    return true;
  }

  onStartTimeChange() {
    if (!this.startDateObj || !this.endDateObj) return;
    if (!this.isSameDay(this.startDateObj, this.endDateObj)) return;

    const startMinutes = this.parseTimeToMinutes(this.startTime);
    const endMinutes = this.parseTimeToMinutes(this.endTime);

    if (startMinutes !== null && endMinutes !== null && endMinutes <= startMinutes) {
      this.endTime = null;
    }
  }

  private combineDateAndTime(dateIso: string | null, time: string | null): string | null {
    if (!dateIso) return null;

    const date = this.parseFromIsoLocalClock(dateIso);

    let hours = 0;
    let minutes = 0;

    if (time) {
      const [timePart, modifier] = time.trim().split(/\s+/);
      const [h, m] = timePart.split(':').map(Number);

      hours = h;
      minutes = m;

      if (modifier) {
        const mod = modifier.toUpperCase();
        if (mod === 'PM' && hours !== 12) {
          hours += 12;
        }
        if (mod === 'AM' && hours === 12) {
          hours = 0;
        }
      }
    }

    date.setHours(hours, minutes, 0, 0);
    return this.parseToIsoLocalCLock(date);
  }

  private formatDisplayDate(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }).format(date);
  }

  private parseFromIsoLocalClock(value: string): Date {
    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(value);
    return new Date(hasTimezone ? value : value + 'Z');
  }

  private parseToIsoLocalCLock(d: Date): string {
    return d.toISOString();
  }
}
